import React, { createContext } from "react";
import PropTypes from "prop-types";
import { initializeApp } from "firebase/app";
import "firebase/auth";
import {
  connectAuthEmulator,
  getAuth,
  signInWithCustomToken,
} from "firebase/auth";
import { connectFirestoreEmulator, getFirestore } from "firebase/firestore";
import { getMessaging } from "firebase/messaging";

// Initialize Firebase
const firebaseApp = initializeApp({
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_APP_FIREBASE_MEASUREMENT_ID,
});

console.warn("Firebase PROJECT mode", import.meta.env.MODE);

const firebaseAuth = getAuth(firebaseApp);
const firestoreDb = getFirestore(firebaseApp);

// getMessaging() throws in an insecure context (e.g. opening the dev server over
// http://<lan-ip> from a phone, where service workers aren't available). It must
// NOT crash app startup — messaging is optional.
let firestoreMessaging = null;
try {
  firestoreMessaging = getMessaging(firebaseApp);
} catch (err) {
  console.warn("Firebase Messaging unavailable (insecure context?):", err);
}
const environment = import.meta.env.MODE;
const rawApiBaseUrl = import.meta.env.VITE_API_BASE_URL;

// Dev/e2e runs against the local Firebase emulators. When the app is opened from
// another device on the LAN (e.g. a phone), a hard-coded `127.0.0.1` would point
// at THAT device, so derive the emulator/API host from the URL the page was
// actually loaded with.
const isEmulator =
  !!rawApiBaseUrl &&
  (rawApiBaseUrl.includes("127.0.0.1") || rawApiBaseUrl.includes("localhost"));
const devHost =
  typeof window !== "undefined" ? window.location.hostname : "127.0.0.1";
const apiBaseUrl = isEmulator
  ? rawApiBaseUrl.replace(/127\.0\.0\.1|localhost/, devHost)
  : rawApiBaseUrl;

if (isEmulator) {
  connectFirestoreEmulator(firestoreDb, devHost, 8080);
  connectAuthEmulator(firebaseAuth, `http://${devHost}:9099`);
}

// Cypress snapshot specs need to sign in as an arbitrary fixture user without
// going through email/password. Only exposed against the emulator, never in prod.
if (isEmulator && typeof window !== "undefined") {
  window.__firebaseAuth = firebaseAuth;
  window.__signInWithCustomToken = signInWithCustomToken;
}

// Create a Firebase context
export const FirebaseContext = createContext({
  firebaseApp,
  firebaseAuth,
  firestoreDb,
  firestoreMessaging,
  environment,
  apiBaseUrl,
});

// Create a Firebase provider component
export const FirebaseProvider = ({ children }) => {
  return (
    <FirebaseContext.Provider
      value={{
        firebaseApp,
        firebaseAuth,
        firestoreDb,
        firestoreMessaging,
        environment,
        apiBaseUrl,
      }}
    >
      {children}
    </FirebaseContext.Provider>
  );
};

export default FirebaseProvider;

FirebaseProvider.displayName = "FirebaseProvider";

FirebaseProvider.propTypes = {
  children: PropTypes.node,
};
